// The guest's receipt (docs/CHECKOUT_API_2026-10-28.md §10): /receipt/{token}, the link
// a text / e-mail receipt carries. No sign-in: the signed, expiring token is the key, and
// the page re-reads the restaurant's books on every open (refunds included).
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '@/config/api';
import { money } from '../lib/format';
import { formatDateTime, parseApiTime } from '@/lib/restaurantTime';
import { C, FONT_TIGHT } from '../theme/tokens';

// on the restaurant's clock (the receipt's `timezone`), never the guest's phone's
function when(iso, tz) {
  if (!parseApiTime(iso)) return null;
  return formatDateTime(iso, tz);
}

export default function PublicReceipt() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let gone = false;
    fetch(`${API}/public/receipts/${encodeURIComponent(token)}`, { headers: { Accept: 'application/json' } })
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (gone) return;
        if (r.ok) setState({ receipt: body });
        else setState({ error: r.status === 410 ? 'This receipt link has expired. Ask the restaurant for a new one.' : r.status === 404 ? 'We could not find this receipt.' : (body?.detail || 'This receipt cannot be shown right now.') });
      })
      .catch(() => { if (!gone) setState({ error: 'Could not load the receipt. Check the connection and try again.' }); });
    return () => { gone = true; };
  }, [token]);

  const r = state.receipt;
  const row = (label, value, opts = {}) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: opts.big ? 18 : 14.5, fontWeight: opts.big ? 800 : 500, color: opts.color || (opts.big ? C.ink : C.ink3), padding: opts.big ? '6px 0 0' : '2px 0' }}>
      <span>{label}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
  return (
    <div style={{ minHeight: '100vh', background: C.paper, display: 'flex', justifyContent: 'center', padding: '28px 16px 48px', fontFamily: "'Inter', system-ui, sans-serif", color: C.ink }}>
      <main style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {state.loading && <div style={{ background: '#FFFFFF', borderRadius: 24, height: 420, boxShadow: '0 20px 50px rgba(28,25,23,.08)' }} aria-busy="true" />}
        {state.error && (
          <div role="alert" style={{ background: '#FFFFFF', borderRadius: 24, padding: 28, textAlign: 'center', boxShadow: '0 20px 50px rgba(28,25,23,.08)' }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>Receipt unavailable</div>
            <div style={{ fontSize: 14.5, color: C.muted, lineHeight: 1.5 }}>{state.error}</div>
          </div>
        )}
        {r && (
          <article data-testid="public-receipt" style={{ background: '#FFFFFF', borderRadius: 24, overflow: 'hidden', boxShadow: '0 20px 50px rgba(28,25,23,.10)' }}>
            <header style={{ background: C.ink, color: '#FFFFFF', padding: '22px 24px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FDBA74' }}>Receipt</span>
              <h1 style={{ margin: 0, fontFamily: FONT_TIGHT, fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px' }}>{r.restaurant?.name}</h1>
              {(r.restaurant?.address || r.restaurant?.phone) && <span style={{ fontSize: 13, color: '#D6D3D1' }}>{[r.restaurant.address, r.restaurant.phone].filter(Boolean).join(' · ')}</span>}
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 12, gap: 12 }}>
                <span style={{ fontSize: 14, color: '#E7E5E4' }}>{r.title}{when(r.paid_at, r.timezone) ? ` · ${when(r.paid_at, r.timezone)}` : ''}</span>
                <span style={{ fontFamily: FONT_TIGHT, fontSize: 30, fontWeight: 800 }}>{money(r.charged_cents)}</span>
              </div>
            </header>
            <section style={{ padding: '16px 24px 6px' }}>
              {r.lines.map((ln, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: `1px solid ${C.hair2}` }}>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{ln.quantity > 1 ? `${ln.quantity}× ` : ''}{ln.name}{ln.share !== '1/1' ? <span style={{ color: C.muted, fontWeight: 500 }}> · {ln.share} share</span> : null}</span>
                    {ln.modifiers?.length > 0 && <span style={{ fontSize: 12.5, color: C.muted }}>{ln.modifiers.join(', ')}</span>}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(ln.amount_cents)}</span>
                </div>
              ))}
            </section>
            <section style={{ padding: '10px 24px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {row('Subtotal', money(r.subtotal_cents))}
              {r.discount_cents > 0 && row('Discount', `−${money(r.discount_cents)}`, { color: C.greenInk })}
              {r.comp_cents > 0 && row('Comp', `−${money(r.comp_cents)}`, { color: C.greenInk })}
              {r.service_charge_cents > 0 && row('Service charge', money(r.service_charge_cents))}
              {row(`Tax${r.tax_rate_percent && Number(r.tax_rate_percent) ? ` ${Number(r.tax_rate_percent)}%` : ''}`, money(r.tax_cents))}
              {r.tip_cents > 0 && row('Tip', money(r.tip_cents))}
              {row('Total', money(r.total_cents), { big: true })}
              {r.fee_cents > 0 && row('Card fee', money(r.fee_cents))}
              {r.fee_cents > 0 && row('Charged', money(r.charged_cents), { big: true })}
            </section>
            <section style={{ padding: '14px 24px 18px', background: C.cream, borderTop: `1px solid ${C.hair}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.faint }}>{r.payment_count > 1 ? `Paid with ${r.payment_count} payments` : 'Paid with'}</span>
              {r.payments.map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14.5, color: p.voided ? C.faint : C.ink2, textDecoration: p.voided ? 'line-through' : 'none' }}>
                  <span>{p.label}{p.tip_cents > 0 ? ` · incl. ${money(p.tip_cents)} tip` : ''}{p.fee_cents > 0 && !p.voided ? ` · fee ${money(p.fee_cents)}` : ''}{p.voided ? ' · voided' : ''}</span>
                  <span style={{ fontWeight: 700 }}>{money(p.amount_cents + p.tip_cents)}</span>
                </div>
              ))}
              {r.payments.length > 0 && (
                <div data-testid="receipt-total-paid" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, color: C.ink, borderTop: `1px solid ${C.hair}`, paddingTop: 6 }}>
                  <span>Total paid</span><span>{money(r.paid_cents)}</span>
                </div>
              )}
              {r.refunds.map((f, i) => (
                <div key={`f${i}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14.5, color: C.redInk }}>
                  <span>Refunded{f.method === 'card' ? ' to the card' : ` (${f.method})`}{when(f.at, r.timezone) ? ` · ${when(f.at, r.timezone)}` : ''}</span>
                  <span style={{ fontWeight: 700 }}>−{money(f.amount_cents)}</span>
                </div>
              ))}
            </section>
          </article>
        )}
        {r && <p style={{ textAlign: 'center', fontSize: 12.5, color: C.faint, margin: 0 }}>Thank you for dining with us{r.link_expires_at ? ` · this link works until ${formatDateTime(r.link_expires_at, r.timezone, { hour: undefined, minute: undefined })}` : ''}</p>}
      </main>
    </div>
  );
}
